import { useRef } from 'react';
import { GetStaticPathsResult, GetStaticPropsContext, GetStaticPropsResult } from 'next';

import { Box, Divider, Heading, Text, useToken } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { Markdown } from '~/components/markdown';
import { SanityImage } from '~/components/sanity-image';
import { ShapesBanner } from '~/components/shapes-banner';
import { PageCategory } from '~/features/analytics/constants';
import { trackEvent } from '~/features/analytics/track-event';
import { EnterpriseResourceContainer } from '~/features/enterprise-resource/components/enterprise-resource-container';
import { EnterpriseResourceForm } from '~/features/enterprise-resource/components/enterprise-resource-form';
import { EnterpriseResourceGlyphCardSection } from '~/features/enterprise-resource/components/enterprise-resource-glyph-card-section';
import { EnterpriseResourceHero } from '~/features/enterprise-resource/components/enterprise-resource-hero';
import { EnterpriseResourceHeroWithLeadForm } from '~/features/enterprise-resource/components/enterprise-resource-hero-with-lead-form';
import { EnterpriseResourceImageBulletedListBlock } from '~/features/enterprise-resource/components/enterprise-resource-image-bulleted-list-block';
import { EnterpriseResourceImpactSection } from '~/features/enterprise-resource/components/enterprise-resource-impact-section';
import { EnterpriseResourceReviewCard } from '~/features/enterprise-resource/components/enterprise-resource-review-card';
import { getEnterpriseReportContent } from '~/features/enterprise-resource/controllers/get-enterprise-report-content';
import { EnterpriseReportContent } from '~/features/enterprise-resource/models/enterprise-report-content';
import { logger } from '~/features/logging/logger';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';

type EnterpriseReportPageProps = {
  page: Page;
  enterpriseReportContent: EnterpriseReportContent;
};

export default function EnterpriseReportPage({ page, enterpriseReportContent }: EnterpriseReportPageProps) {
  const [containerWidth] = useToken('sizes', ['content-max-width']);
  const formRef = useRef<HTMLDivElement>(null);
  const reportData = enterpriseReportContent.reportData;
  const formData = reportData.form;
  const fileUrl = reportData.pdfFile.asset.url;

  function handlePrimaryCtaClick() {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth' });
    }

    if (!reportData.isGated && reportData.shapesBanner?.primaryCta?.buttonAction === 'Coded Action') {
      window.open(fileUrl, '_blank');
    }

    trackEvent({
      type: 'click',
      name: 'Enterprise Report CTA Clicked',
      label: 'shapes_banner_cta_clicked',
      value: enterpriseReportContent.slug,
    });
  }

  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.EnterpriseReport} />
      <PageLayout overflowX='hidden' withoutVerticalPadding>
        {reportData.isGated && formData && (
          <EnterpriseResourceHeroWithLeadForm
            description={reportData.description}
            image={reportData.heroImage}
            relatedCompanies={reportData.relatedCompanies}
            resourceTypeTitle={reportData.resourceTypeTitle}
            title={reportData.title}
            bottomLeftSlot={
              reportData.impactTitle &&
              reportData.impactHighlights && (
                <Box backgroundColor='blue.700' borderRadius='0px 0px 0px 64px' height='100%' width='100%' />
              )
            }
            contentSlot={
              <>
                {reportData.impactTitle && reportData.impactHighlights && !reportData.heroImage && (
                  <EnterpriseResourceImpactSection
                    impactHighlights={reportData.impactHighlights}
                    impactTitle={reportData.impactTitle}
                  />
                )}
              </>
            }
            formSlot={
              <Box marginBottom={{ base: '0px', lg: '48px' }}>
                <EnterpriseResourceForm
                  ref={formRef}
                  additionalEventProperties={{ reportName: enterpriseReportContent.slug }}
                  fileUrl={fileUrl}
                  leadCaptureType={reportData.leadCaptureType}
                  resourceType='report'
                  templateForm={formData}
                />
              </Box>
            }
          />
        )}

        {!reportData.isGated && reportData.heroImage && reportData.primaryCta && (
          <>
            <EnterpriseResourceHero
              description={reportData.description}
              fileUrl={fileUrl}
              image={reportData.heroImage}
              primaryCta={reportData.primaryCta}
              relatedCompanies={reportData.relatedCompanies}
              resourceTypeTitle={reportData.resourceTypeTitle}
              title={reportData.title}
            />

            {reportData.impactTitle && reportData.impactHighlights && (
              <Box backgroundColor='blue.700' borderRadius='0px 0px 0px 64px' overflow='hidden'>
                <EnterpriseResourceContainer>
                  <EnterpriseResourceImpactSection
                    impactHighlights={reportData.impactHighlights}
                    impactTitle={reportData.impactTitle}
                  />
                </EnterpriseResourceContainer>
              </Box>
            )}
          </>
        )}

        {reportData.pageContentList && reportData.pageImage && reportData.pageContentTitle && (
          <EnterpriseResourceContainer marginTop={{ base: '40px', md: '64px', lg: '80px' }}>
            <EnterpriseResourceImageBulletedListBlock
              contentList={reportData.pageContentList}
              image={reportData.pageImage}
              title={reportData.pageContentTitle}
            />
          </EnterpriseResourceContainer>
        )}

        {reportData.insightsList && reportData.insightsTitle && (
          <EnterpriseResourceContainer marginTop={{ base: '64px', lg: '120px' }}>
            <EnterpriseResourceGlyphCardSection cardList={reportData.insightsList} title={reportData.insightsTitle} />
          </EnterpriseResourceContainer>
        )}

        {reportData.imageContentBlock && (
          <EnterpriseResourceContainer marginTop={{ base: '40px', md: '64px', lg: '80px' }} textAlign='center'>
            <Heading as='h2' margin='auto' size='marketing-4xl'>
              {reportData.imageContentBlock.title}
            </Heading>

            <Box margin='auto' marginTop='24px' maxWidth='736px' textAlign='center'>
              <Text>{reportData.imageContentBlock.text}</Text>
            </Box>

            <Box marginTop='40px' marginX={{ base: 'unset', lg: '96px' }}>
              <SanityImage
                image={reportData.imageContentBlock.image}
                sizes={`(min-width: 992px) ${containerWidth}px, 100vw`}
                style={{
                  objectFit: 'contain',
                  width: '100%',
                }}
              />
            </Box>
          </EnterpriseResourceContainer>
        )}

        {reportData.reviewsTitle && reportData.reviewsList && (
          <>
            <EnterpriseResourceContainer marginTop={{ base: '40px', md: '64px', lg: '80px' }}>
              <Heading
                as='h3'
                marginBottom={{ base: '16px', md: '0px' }}
                marginRight={{ base: '0px', md: '40px' }}
                size='marketing-2xl'
              >
                {reportData.reviewsTitle}
              </Heading>
            </EnterpriseResourceContainer>

            <Box marginTop='40px' overflow='auto'>
              <EnterpriseResourceContainer display='flex' gap={{ base: '16px', md: '32px' }}>
                {reportData.reviewsList.map((review) => (
                  <Box
                    key={review.person.name}
                    sx={{
                      ':last-of-type > *': { marginRight: { base: '16px', md: '124px', lg: '48px' } },
                    }}
                  >
                    <EnterpriseResourceReviewCard
                      minWidth={{ base: '300px', lg: '352px' }}
                      review={review}
                      width={{ base: '300px', lg: '352px' }}
                    />
                  </Box>
                ))}
              </EnterpriseResourceContainer>
            </Box>
          </>
        )}

        {reportData.aboutContentTitle && reportData.aboutContentDescription && (
          <ContentContainer>
            <Divider marginY={{ base: '40px', md: '64px', lg: '80px' }} />

            <Box display='flex' flexWrap={{ base: 'wrap', md: 'nowrap' }} marginX={{ base: '0px', lg: '96px' }}>
              <Heading
                as='h3'
                marginBottom={{ base: '16px', md: '0px' }}
                marginRight={{ base: '0px', md: '40px' }}
                size='marketing-2xl'
                whiteSpace='nowrap'
              >
                {reportData.aboutContentTitle}
              </Heading>

              <Markdown markdownText={reportData.aboutContentDescription} />
            </Box>
          </ContentContainer>
        )}

        <Box h={{ base: '80px', md: '120px' }} />

        {reportData.shapesBanner && (
          <ShapesBanner
            {...reportData.shapesBanner}
            flipTitleAndSubtitleOrder={true}
            onPrimaryCtaClick={handlePrimaryCtaClick}
          />
        )}
      </PageLayout>
    </>
  );
}

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return {
    fallback: 'blocking',
    paths: [],
  };
}

export async function getStaticProps({
  params,
}: GetStaticPropsContext<{ slug: string }>): Promise<GetStaticPropsResult<EnterpriseReportPageProps>> {
  const slug = params?.slug;

  if (!slug) {
    logger.fatal('Enterprise report slug not found', {
      file: 'src/pages/report/[slug].tsx',
      function: 'getStaticProps',
      slug,
    });

    throw new Error('Enterprise report slug not found');
  }

  const [page, enterpriseReportContent] = await Promise.all([getPage('/report'), getEnterpriseReportContent(slug)]);

  if (!enterpriseReportContent) {
    return {
      notFound: true,
    };
  }

  // If enterpriseReportContent has seo, override default page seo
  if (enterpriseReportContent.seo) {
    page.seo = enterpriseReportContent.seo;
  }

  return {
    props: {
      page,
      enterpriseReportContent,
    },
  };
}
